import { FC, useRef, useState, useMemo } from 'react'
import styled from 'styled-components'
import { Box, Flex } from '@/components/atoms/Grid'
import useTranslation from 'next-translate/useTranslation'
import { SearchIconNewDefault } from '@/components/atoms/icons/SearchIconDefaultNew'
import { ClearIconDefault } from '@/components/atoms/icons/ClearIconDefault'
import CheckboxFilterRowRedesign from './CheckboxFilterRowRedesign'
import { Small } from '@/components/atoms/typography'

export type MobileFilterItemType = {
  id: string
  title: string
  count?: number
  value?: string
}

type MobileFilterGridItemProps = {
  items: MobileFilterItemType[]
  selectedIds: string[]
  onSelectionChange: (ids: string[]) => void
  withSearch?: boolean
  searchPlaceholder?: string
  isColorVariant?: boolean
}

const MobileFilterGridItem: FC<MobileFilterGridItemProps> = ({
  items,
  selectedIds,
  onSelectionChange,
  withSearch = false,
  searchPlaceholder,
  isColorVariant = false
}) => {
  const { t } = useTranslation('common')
  const [searchText, setSearchText] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  const filteredItems = useMemo(() => {
    if (!searchText) {
      return items
    }
    const lowerSearch = searchText.toLowerCase()
    return items.filter((item) =>
      item.title?.toLowerCase().includes(lowerSearch)
    )
  }, [items, searchText])

  const handleToggle = (id: string) => {
    let newSelected: string[]
    if (selectedIds.includes(id)) {
      newSelected = selectedIds.filter((i) => i !== id)
    } else {
      newSelected = [...selectedIds, id]
    }
    onSelectionChange(newSelected)
  }

  const clearInput = () => {
    setSearchText('')
    inputRef.current?.focus()
  }

  return (
    <>
      {withSearch && (
        <SearchWrapper>
          <SearchInputBox>
            <SearchButton
              aria-label="Open search bar"
              type="button"
              onClick={() => inputRef.current?.focus()}
            >
              <SearchIconNewDefault />
            </SearchButton>
            <SearchInput
              ref={inputRef}
              value={searchText}
              placeholder={searchPlaceholder || t('Category.Filter.searching')}
              onChange={(e) => setSearchText(e.target.value)}
            />
            {searchText && (
              <StyledClearButton type="button" onClick={clearInput}>
                <ClearIconDefault />
              </StyledClearButton>
            )}
          </SearchInputBox>
        </SearchWrapper>
      )}

      <ScrollableContent>
        {filteredItems.map((item) => (
          <CheckboxFilterRowRedesign
            key={item.id}
            title={
              item.title
                ? item.title.charAt(0).toUpperCase() + item.title.slice(1)
                : ''
            }
            count={item.count || 0}
            isChecked={selectedIds.includes(item.id)}
            onCheckboxClick={() => handleToggle(item.id)}
            color={isColorVariant ? item.value : undefined}
            size={20}
          />
        ))}
        {filteredItems.length === 0 && searchText && (
          <Box p={16} textAlign="center">
            <Small>{t('Category.Filter.noResults')}</Small>
          </Box>
        )}
      </ScrollableContent>
    </>
  )
}

export default MobileFilterGridItem

const SearchWrapper = styled.div`
  padding: 16px 4px;
  border-bottom: 1px solid ${(props) => props.theme.colors.navMenuGrey};
  margin-bottom: 8px;
`

const SearchInputBox = styled(Flex)`
  max-width: 100%;
  position: relative;
`

const SearchInput = styled.input`
  flex: 1;
  border: none;
  border-radius: 4px;
  background: ${(props) => props.theme.colors.navMenuGrey};
  outline: none;
  padding: 5px 32px 5px 42px;
  font-size: 14px;
  box-sizing: border-box;
  height: 36px;

  &::placeholder {
    color: #757575;
  }
  &:focus {
    outline-style: none;
  }
`

const SearchButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  position: absolute;
  left: 1px;
  top: 50%;
  transform: translateY(-50%);
  z-index: 1;

  & svg {
    fill: ${(props) => props.theme.colors.searchButton};
    width: 14px;
  }
`

const StyledClearButton = styled.div`
  border-radius: 50%;
  background: none;
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  z-index: 1;
  cursor: pointer;

  & svg {
    width: 14px;
    height: 14px;
  }
`

const ScrollableContent = styled(Box)`
  padding-block: 8px;
`
